import { BehaviorSubject, from, of, ReplaySubject, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { Inject, Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Environment } from '@elight/client/shared/utility/common';
// tslint:disable:nx-enforce-module-boundaries
import { UserDTO } from '@elight/client/shared/data-access/users';
import {
    DatabaseEventType,
    DatabaseResource,
    DatabaseStateService,
} from '@elight/client/shared/utility/state';
// tslint:enable:nx-enforce-module-boundaries

import { Division, User, UserCredentials } from './auth.interface';
import { Firebase } from './firebase.provider';

@Injectable()
export class AuthService {
    private user$ = new ReplaySubject<User | null>(1);
    private isFbReadyStream = new ReplaySubject<boolean>(1);
    private accessMatrixStream = new BehaviorSubject<any>({});

    constructor(
        @Inject(Firebase) private firebase: any,
        private zone: NgZone,
        private httpClient: HttpClient,
        private env: Environment,
        private state: DatabaseStateService
    ) {
        const firebaseConfig = this.env.firebase;
        this.firebase.initializeApp(firebaseConfig);
        if (this.env.development) {
            this.firebase.auth().useEmulator('http://localhost:9099');
        }

        this.firebase.auth().onAuthStateChanged(async (authState) => {
            this.isFbReadyStream.next(true);
            if (authState) {
                try {
                    this.loadCurrentUser().subscribe(() => {});
                } catch (e) {
                    this.handleAuthError();
                }
            } else {
                this.user$.next(null);
                this.accessMatrixStream.next({});
            }
        });
    }

    public get currentUser$() {
        return this.user$.asObservable();
    }

    public login({ email, password }: UserCredentials) {
        return from(
            this.zone.run(() =>
                this.firebase.auth().signInWithEmailAndPassword(email, password)
            )
        ).pipe(
            switchMap(() => this.loadCurrentUser()),
            catchError(this.onAuthError.bind(this))
        );
    }

    public get accessMatrix() {
        return this.accessMatrixStream.value;
    }

    /**
     * @param selector: String formatted as `page.field.rule` e.g. `project.name.hidden`
     */
    public hasAccessToField(selector: string) {
        const exp = /\w+\.{1}\w+\.{1}\w+/g;
        if (!selector || !selector.match(exp)) {
            return false;
        }

        const [pageKey, fieldKey, rule] = selector.split('.');
        const page = this.accessMatrix[pageKey];

        //if the fields are not defined use the page rule for all fields
        const field = page?.fields ? page.fields[fieldKey] : page;

        // Fields are hidden = true, readonly = true so in order to use show = true/false we need to negate.
        // We also cast to bool in case it's falsy
        // e.g. hidden = true -> show = false;
        // If the rule is not defined for the particular field use the global page rule instead.
        return field ? !!!field[rule] : !!!page[rule];
    }

    public acceptInvite(payload: {
        code: string;
        password: string;
        displayName: string;
    }) {
        const url = `${this.env.api.baseURL}/registration/invite`;
        return this.httpClient.post(`${url}`, payload);
    }

    public acceptInviteExistingUser(payload: { code: string }) {
        const url = `${this.env.api.baseURL}/registration/invite/existing`;
        return this.httpClient.post(url, payload);
    }

    public register({ email, password }: UserCredentials) {
        return from(
            this.zone.run(() =>
                this.firebase
                    .auth()
                    .createUserWithEmailAndPassword(email, password)
            )
        ).pipe(catchError(this.onAuthError.bind(this)));
    }

    public resetPassword(email: string) {
        return from(
            this.zone.run(() =>
                this.firebase.auth().sendPasswordResetEmail(email, {
                    url: `${window.location.origin}/auth/login`,
                    handleCodeInApp: false,
                })
            )
        );
    }

    public logout() {
        return from(this.zone.run(() => this.firebase.auth().signOut())).pipe(
            catchError(this.onAuthError.bind(this))
        );
    }

    public getToken() {
        return this.isFbReadyStream.asObservable().pipe(
            switchMap(() =>
                from(this.firebase.auth().currentUser.getIdToken())
            ),
            catchError(() => of(null)),
            take(1)
        );
    }

    public hasRole(requiredRoles: string[]) {
        return this.currentUser$.pipe(
            map((user) => requiredRoles.indexOf(user?.roles[0]) >= 0),
            take(1)
        );
    }

    public changeDivision({ id }: Division) {
        const url = `${this.env.api.baseURL}/users/me/switch_division`;
        return this.httpClient
            .post(url, {
                division_id: id,
            })
            .pipe(
                switchMap(() =>
                    from(this.firebase.auth().currentUser.getIdToken(true))
                ),
                switchMap(() => this.loadCurrentUser()),
                tap((user) => {
                    this.user$.next(user);
                    this.accessMatrixStream.next(user.uiPermissions);
                    this.state.emitEvent({
                        type: DatabaseEventType.USER_CHANGED,
                        resource: DatabaseResource.ANY,
                    });
                })
            );
    }

    private loadCurrentUser() {
        return this.getCurrentUser().pipe(
            map(this.mapToUserState),
            tap((user) => {
                this.user$.next(user);
                this.accessMatrixStream.next(user.uiPermissions);
            })
        );
    }

    private getCurrentUser() {
        const url = `${this.env.api.baseURL}/users/me`;
        return this.httpClient.get(url);
    }

    private mapToUserState(me: UserDTO) {
        return {
            email: me.user.email,
            firebaseId: me.user.firebase_id,
            id: me.user.id,
            displayName: me.user.display_name,
            roles: me.user.roles,
            currentDivisionId: me.current_division_id,
            currentDivision: me.current_division,
            divisions: me.divisions,
            currency: me.currency,
            uiPermissions: me.ui_permissions,
        };
    }

    private onAuthError(err: Error) {
        this.handleAuthError();
        return throwError(err);
    }

    private handleAuthError() {
        // logout on error
        // @TODO: are we doing any FE logging?
        this.user$.next(null);
        this.accessMatrixStream.next({});
    }
}
